import React, { useState, useEffect, useRef, Fragment } from 'react'
import { Link } from 'react-router-dom'
import {
	PencilIcon, TrashIcon, CheckIcon, XIcon,
	CheckCircleIcon, ExclamationCircleIcon, HomeIcon,
	PlusIcon, SearchIcon, AdjustmentsIcon,
	LocationMarkerIcon, CollectionIcon, UsersIcon, CogIcon,
	ChevronLeftIcon, ChevronRightIcon, RefreshIcon
} from '@heroicons/react/outline'
import { motion } from 'framer-motion'
import connexion from '../../connexion'
import Loader from '../UI/Loader'
import './Pitches.css'

const ITEMS_PER_PAGE = 12

const SPECIAL_TYPE_OPTIONS = [
	'Local Technique',
	'Espace Aquatique',
	'Restauration / Commerce',
	'Sanitaires / Bloc',
	'Sport / Loisirs',
	'Administration / Accueil',
	'Espace Vert',
	'Autre'
]

export default function Pitches({ updateNavbarTitle })
{
	// Data
	const [pitches, setPitches] = useState(null)
	const [error, setError] = useState(null)

	// Edit inline
	const [editing_id, setEditingId] = useState(null)
	const [edit_form, setEditForm] = useState({})
	const [edit_free_category, setEditFreeCategory] = useState(false)
	const [edit_free_zone, setEditFreeZone] = useState(false)
	const [edit_free_type, setEditFreeType] = useState(false)
	const [category_options, setCategoryOptions] = useState([])
	const [zone_options, setZoneOptions] = useState([])
	const [type_options, setTypeOptions] = useState([])
	const [attempt_save, setAttemptSave] = useState(false)

	// Delete modal
	const [modal_delete, setModalDelete] = useState({ isOpen: false, pitch_id: null, submitting: false })

	// Toast
	const [toast, setToast] = useState(null)
	const toast_timer = useRef(null)

	// Search / filter / pagination
	const [search_term, setSearchTerm] = useState('')
	const [selected_type, setSelectedType] = useState('all')
	const [selected_zone, setSelectedZone] = useState('all')
	const [current_page, setCurrentPage] = useState(1)

	// Panneau création (UI only — à brancher côté serveur)
	const [show_add_form, setShowAddForm] = useState(false)
	const [form_closing, setFormClosing] = useState(false)
	const form_close_timer = useRef(null)

	const closeAddForm = () =>
	{
		setFormClosing(true)
		setNewPitchZoneFree(false)
		clearTimeout(form_close_timer.current)
		form_close_timer.current = setTimeout(() =>
		{
			setShowAddForm(false)
			setFormClosing(false)
		}, 200)
	}
	const [form_is_custom, setFormIsCustom] = useState(true)
	const [new_pitch_zone_free, setNewPitchZoneFree] = useState(false)
	const [new_pitch, setNewPitch] = useState({
		name: '',
		category: 'Local Technique',
		zone: '—',
		type: 'intervention',
		capacity: ''
	})

	useEffect(() =>
	{
		updateNavbarTitle('Paramétrage des emplacements')
		loadPitches()
		return () =>
		{
			clearTimeout(toast_timer.current)
			clearTimeout(form_close_timer.current)
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const loadPitches = () =>
	{
		fetch(connexion.connect.url + 'dashboard/pitches/list')
			.then(res => res.json())
			.then(data =>
			{
				if (data === false)
					setError('Impossible de charger les emplacements.')
				else
					setPitches(data)
			})
			.catch(err =>
			{
				console.log(err)
				setError('Impossible de charger les emplacements.')
			})
	}

	const showToast = (message, type = 'success') =>
	{
		setToast({ message, type })
		clearTimeout(toast_timer.current)
		toast_timer.current = setTimeout(() => setToast(null), 4000)
	}

	const startEdit = (pitch) =>
	{
		const cats = [...new Set(pitches.filter(p => p.location_category != null).map(p => p.location_category))].sort()
		const zones = [...new Set(pitches.filter(p => p.zone != null).map(p => p.zone))].sort()
		const types = [...new Set(pitches.filter(p => p.type != null).map(p => p.type))].sort()
		setCategoryOptions(cats)
		setZoneOptions(zones)
		setTypeOptions(types)
		setEditingId(String(pitch._id))
		setEditForm({
			location_category: pitch.location_category != null ? pitch.location_category : '',
			zone: pitch.zone != null ? pitch.zone : '',
			type: pitch.type != null ? pitch.type : '',
			special_type: pitch.special_type != null ? pitch.special_type : '',
			capacity: pitch.capacity != null ? String(pitch.capacity) : ''
		})
		setEditFreeCategory(false)
		setEditFreeZone(false)
		setEditFreeType(false)
	}

	const cancelEdit = () =>
	{
		setEditingId(null)
		setEditForm({})
		setEditFreeCategory(false)
		setEditFreeZone(false)
		setEditFreeType(false)
	}

	const handleEditChange = (field, value) =>
	{
		setEditForm(prev => ({ ...prev, [field]: value }))
	}

	const handleSelectChange = (field, setFreeFlag, value) =>
	{
		if (value === '__autre__')
		{
			setEditForm(prev => ({ ...prev, [field]: '' }))
			setFreeFlag(true)
		}
		else
		{
			setEditForm(prev => ({ ...prev, [field]: value === '__none__' ? '' : value }))
			setFreeFlag(false)
		}
	}

	const saveEdit = (pitch_id) =>
	{
		setAttemptSave(true)
		fetch(connexion.connect.url + 'dashboard/pitches/update', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				id: pitch_id,
				location_category: edit_form.location_category !== '' ? edit_form.location_category : null,
				zone: edit_form.zone !== '' ? edit_form.zone : null,
				type: edit_form.type !== '' ? edit_form.type : null,
				special_type: edit_form.special_type !== '' ? edit_form.special_type : null,
				capacity: edit_form.special_type ? null : (edit_form.capacity !== '' ? Number(edit_form.capacity) : null)
			})
		})
		.then(res => res.json())
		.then(ret =>
		{
			setAttemptSave(false)
			setEditingId(null)
			setEditForm({})
			if (ret !== false)
			{
				showToast('Emplacement mis à jour.')
				loadPitches()
			}
			else
				showToast('Erreur lors de la mise à jour.', 'error')
		})
		.catch(err =>
		{
			console.log(err)
			setAttemptSave(false)
			showToast('Erreur lors de la mise à jour.', 'error')
		})
	}

	const openDeleteModal = (pitch_id) =>
	{
		setModalDelete({ isOpen: true, pitch_id, submitting: false })
	}

	const confirmDelete = () =>
	{
		setModalDelete(prev => ({ ...prev, submitting: true }))
		fetch(connexion.connect.url + 'dashboard/pitches/delete', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: modal_delete.pitch_id })
		})
		.then(res => res.json())
		.then(ret =>
		{
			setModalDelete({ isOpen: false, pitch_id: null, submitting: false })
			if (ret !== false)
			{
				showToast('Emplacement supprimé.')
				loadPitches()
			}
			else
				showToast('Erreur lors de la suppression.', 'error')
		})
		.catch(err =>
		{
			console.log(err)
			setModalDelete({ isOpen: false, pitch_id: null, submitting: false })
			showToast('Erreur lors de la suppression.', 'error')
		})
	}

	// ---- Valeurs calculées ----

	const filtered_pitches = pitches === null ? [] : pitches.filter(p =>
	{
		const q = search_term.toLowerCase()
		const matches_search = search_term === '' || (
			(p.location || '').toLowerCase().includes(q) ||
			(p.location_category || '').toLowerCase().includes(q) ||
			(p.zone || '').toLowerCase().includes(q) ||
			(p.type || '').toLowerCase().includes(q)
		)
		const matches_type = selected_type === 'all' || p.type === selected_type
		const matches_zone = selected_zone === 'all' || p.zone === selected_zone
		return matches_search && matches_type && matches_zone
	})

	const total_pages = Math.max(1, Math.ceil(filtered_pitches.length / ITEMS_PER_PAGE))
	const safe_page = Math.min(current_page, total_pages)
	const paginated = filtered_pitches.slice((safe_page - 1) * ITEMS_PER_PAGE, safe_page * ITEMS_PER_PAGE)

	const unique_categories = pitches === null ? [] : [...new Set(pitches.filter(p => p.location_category).map(p => p.location_category))]
	const unique_zones_for_filter = pitches === null ? [] : [...new Set(pitches.filter(p => p.zone != null).map(p => p.zone))].sort()
	const unique_zones_count = pitches === null ? 0 : [...new Set(pitches.filter(p => p.zone && p.zone !== '—').map(p => p.zone))].length
	const total_capacity = pitches === null ? 0 : pitches.filter(p => !(p.type === null && p.special_type != null)).reduce((sum, p) => sum + (p.capacity || 0), 0)
	const custom_count = pitches === null ? 0 : pitches.filter(p => p.type === null && p.special_type != null).length
	const unique_types_for_filter = pitches === null ? [] : [...new Set(pitches.filter(p => p.type).map(p => p.type))].sort()

	const has_filters = search_term !== '' || selected_type !== 'all' || selected_zone !== 'all'

	const handleSetPage = (p) =>
	{
		if (p >= 1 && p <= total_pages)
			setCurrentPage(p)
	}

	const handleAddPitch = (e) =>
	{
		e.preventDefault()
		if (!new_pitch.name.trim())
		{
			showToast('Le nom est requis.', 'error')
			return
		}
		const body = form_is_custom
			? {
				location: new_pitch.name.trim(),
				zone: (new_pitch.zone === '—' || new_pitch.zone === '') ? null : new_pitch.zone,
				special_type: new_pitch.category,
				type: null,
				location_category: null,
				capacity: null
			}
			: {
				location: new_pitch.name.trim(),
				zone: (new_pitch.zone === '—' || new_pitch.zone === '') ? null : new_pitch.zone,
				type: new_pitch.type || null,
				location_category: new_pitch.category || null,
				capacity: new_pitch.capacity !== '' ? Number(new_pitch.capacity) : null,
				special_type: null
			}
		fetch(connexion.connect.url + 'dashboard/pitches/insert', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body)
		})
		.then(res => res.json())
		.then(ret =>
		{
			if (ret !== false)
			{
				showToast(form_is_custom ? "Lieu d'intervention créé." : 'Emplacement créé.')
				closeAddForm()
				loadPitches()
			}
			else
				showToast('Erreur lors de la création.', 'error')
		})
		.catch(err =>
		{
			console.log(err)
			showToast('Erreur lors de la création.', 'error')
		})
	}

	const switchFormMode = (is_custom) =>
	{
		setFormIsCustom(is_custom)
		setNewPitchZoneFree(false)
		setNewPitch(is_custom
			? { name: '', category: 'Local Technique', zone: '—', type: 'intervention', capacity: '' }
			: { name: '', category: unique_categories[0] || '', zone: '—', type: 'location', capacity: '' }
		)
	}

	const resetFilters = () =>
	{
		setSearchTerm('')
		setSelectedType('all')
		setSelectedZone('all')
		setCurrentPage(1)
	}

	// ---- Select avec mode "Autre..." ----

	const renderSelectField = (field, is_free, setFreeFlag, options) =>
	{
		const current_value = edit_form[field] || ''

		if (is_free)
		{
			return (
				<div className="flex items-center gap-1.5">
					<input
						type="text"
						value={current_value}
						onChange={e => handleEditChange(field, e.target.value)}
						className="pitches-input flex-1"
						autoFocus
					/>
					<button
						type="button"
						onClick={() =>
						{
							setFreeFlag(false)
							setEditForm(prev => ({ ...prev, [field]: '' }))
						}}
						className="p-1.5 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-600"
						style={{ backgroundImage: 'none', backgroundColor: 'white' }}
						title="Retour à la liste"
					>
						<RefreshIcon className="h-3.5 w-3.5" />
					</button>
				</div>
			)
		}

		const select_value = current_value === '' ? '__none__' : current_value
		return (
			<select
				value={select_value}
				onChange={e => handleSelectChange(field, setFreeFlag, e.target.value)}
				className="pitches-select"
			>
				<option value="__none__">—</option>
				{options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
				<option value="__autre__">Autre…</option>
			</select>
		)
	}

	// ---- Badge type ----

	const renderTypeBadge = (pitch) =>
	{
		const isSpecial = pitch.type === null && pitch.special_type != null
		const label = isSpecial ? 'Zones communes' : (pitch.type || '—')
		const styles = {
			location: 'bg-slate-100 text-slate-700 border-slate-200',
			resident: 'bg-indigo-100 text-indigo-700 border-indigo-200',
			nu: 'bg-amber-50 text-amber-800 border-amber-100'
		}
		const cls = isSpecial ? 'bg-purple-100 text-purple-700 border-purple-200' : (styles[pitch.type] || 'bg-slate-100 text-slate-600 border-slate-200')
		return (
			<span className={`inline-block text-sm font-bold uppercase tracking-tight px-2 py-0.5 rounded border ${cls}`}>
				{label}
			</span>
		)
	}

	// ---- Ligne du tableau ----

	const renderRow = (pitch) =>
	{
		const pitch_id = String(pitch._id)
		const is_editing = editing_id === pitch_id

		if (is_editing)
		{
			return (
				<tr key={pitch_id} className="bg-indigo-50 bg-opacity-40 border-l-2 border-indigo-400">
					<td className="px-6 py-3 text-sm font-bold text-slate-800 font-mono uppercase whitespace-nowrap">
						<span className="flex items-center gap-1.5">
							{(pitch.type === null && pitch.special_type != null) && <LocationMarkerIcon className="h-3.5 w-3.5 text-purple-500 shrink-0" />}
							{pitch.location}
						</span>
					</td>
					<td className="px-3 py-2">
						{pitch.special_type != null
							? <select
								value={edit_form.special_type || ''}
								onChange={e => setEditForm(prev => ({ ...prev, special_type: e.target.value }))}
								className="pitches-select"
							>
								{SPECIAL_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
							</select>
							: renderSelectField('location_category', edit_free_category, setEditFreeCategory, category_options)
						}
					</td>
					<td className="px-3 py-2">
						{renderSelectField('zone', edit_free_zone, setEditFreeZone, zone_options)}
					</td>
					<td className="px-3 py-2">
						{pitch.special_type != null
							? <span className="inline-block text-sm font-bold uppercase tracking-tight px-2 py-0.5 rounded border bg-purple-100 text-purple-700 border-purple-200">Zones communes</span>
							: renderSelectField('type', edit_free_type, setEditFreeType, type_options)
						}
					</td>
					<td className="px-3 py-2">
						{pitch.special_type != null
							? <span className="text-sm font-bold text-purple-600 bg-purple-50 border border-purple-100 px-2 py-0.5 rounded">Hors-réservation</span>
							: <input
								type="number"
								value={edit_form.capacity}
								onChange={e => handleEditChange('capacity', e.target.value)}
								className="pitches-input w-20"
							/>
						}
					</td>
					<td className="px-6 py-3 text-right whitespace-nowrap">
						<div className="flex items-center gap-2 justify-end">
							<button
								onClick={() => saveEdit(pitch_id)}
								disabled={attempt_save}
								className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-bold text-white rounded-lg disabled:opacity-60"
								style={{ backgroundImage: 'none', backgroundColor: '#4f46e5' }}
							>
								{attempt_save
									? <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
										<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
									</svg>
									: <CheckIcon className="h-3.5 w-3.5" />
								}
								Enregistrer
							</button>
							<button
								onClick={cancelEdit}
								disabled={attempt_save}
								className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-60"
								style={{ backgroundImage: 'none', backgroundColor: 'white' }}
							>
								<XIcon className="h-3.5 w-3.5" />
							</button>
						</div>
					</td>
				</tr>
			)
		}

		return (
			<tr key={pitch_id} className="hover:bg-slate-50 transition-colors duration-150">
				<td className="px-6 py-4 text-sm font-bold text-slate-800 font-mono uppercase whitespace-nowrap">
					<span className="flex items-center gap-1.5">
						{(pitch.type === null && pitch.special_type != null) && <LocationMarkerIcon className="h-3.5 w-3.5 text-purple-500 shrink-0" />}
						{pitch.location}
					</span>
				</td>
				<td className="px-6 py-4 text-sm font-semibold text-slate-700 whitespace-nowrap">
					{(pitch.type === null && pitch.special_type != null)
						? <span className="inline-block text-sm font-bold px-2 py-0.5 rounded border bg-purple-100 text-purple-700 border-purple-200">{pitch.special_type}</span>
						: (pitch.location_category != null ? pitch.location_category : '—')
					}
				</td>
				<td className="px-6 py-4 whitespace-nowrap">
					{pitch.zone != null
						? <span className="inline-block px-2 py-0.5 rounded text-sm font-bold font-mono bg-emerald-50 border border-emerald-100 text-slate-700">{pitch.zone}</span>
						: <span className="inline-block px-2 py-0.5 rounded text-sm font-bold font-mono bg-slate-50 border border-slate-100 text-slate-400">—</span>
					}
				</td>
				<td className="px-6 py-4 whitespace-nowrap">
					{renderTypeBadge(pitch)}
				</td>
				<td className="px-6 py-4 whitespace-nowrap">
					{(pitch.type === null && pitch.special_type != null)
						? <span className="text-sm font-bold text-purple-600 bg-purple-50 border border-purple-100 px-2 py-0.5 rounded">Hors-réservation</span>
						: pitch.capacity != null
							? <span className="text-sm font-bold text-slate-700 font-mono">{pitch.capacity} <span className="text-slate-400 font-sans font-normal">pers.</span></span>
							: <span className="text-slate-400 text-sm">—</span>
					}
				</td>
				<td className="px-6 py-4 text-right whitespace-nowrap">
					<div className="flex items-center gap-1 justify-end">
						<button
							onClick={() => startEdit(pitch)}
							className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
							style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
							title="Modifier"
						>
							<PencilIcon className="h-3.5 w-3.5" />
						</button>
						<button
							onClick={() => openDeleteModal(pitch_id)}
							className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
							style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
							title="Supprimer"
						>
							<TrashIcon className="h-3.5 w-3.5" />
						</button>
					</div>
				</td>
			</tr>
		)
	}

	// ---- États de chargement ----

	if (error !== null)
	{
		return (
			<div className="wrapper-content">
				<p className="error center">{error}</p>
			</div>
		)
	}

	if (pitches === null)
	{
		return (
			<div className="loader">
				<Loader />
			</div>
		)
	}

	return (
		<Fragment>

			{/* Toast */}
			{toast !== null && (
					<motion.div
						initial={{ opacity: 0, y: -16, scale: 0.95 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						className="fixed top-6 right-6 z-50 flex items-center gap-3 bg-slate-900 text-white px-5 py-3.5 rounded-xl shadow-2xl border border-slate-800 pointer-events-auto"
					>
						{toast.type === 'success'
							? <CheckCircleIcon className="h-5 w-5 text-green-400 shrink-0" />
							: <ExclamationCircleIcon className="h-5 w-5 text-red-400 shrink-0" />
						}
						<span className="text-sm font-bold">{toast.message}</span>
						<button
							onClick={() => setToast(null)}
							className="text-slate-500 hover:text-white ml-1"
							style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
						>
							<XIcon className="h-4 w-4" />
						</button>
					</motion.div>
				)}

			<div className="wrapper-content">

				{/* Breadcrumb */}
				<div className="mb-5">
					<ol className="flex items-center gap-1 text-sm">
						<li className="flex items-center">
							<Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors">
								<HomeIcon className="flex-shrink-0 h-4 w-4" aria-hidden="true" />
								<span className="sr-only">Accueil</span>
							</Link>
						</li>
						<li className="flex items-center">
							<span className="text-gray-300 mx-1 select-none">/</span>
							<Link to="/emplacements" className="text-gray-400 hover:text-gray-600 transition-colors">Emplacements</Link>
						</li>
						<li className="flex items-center">
							<span className="text-gray-300 mx-1 select-none">/</span>
							<span className="text-gray-400">Paramétrage</span>
						</li>
					</ol>
				</div>

				{/* Header */}
				<div className="flex items-end justify-between mb-8">
					<div>
						<h1
							style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: '-0.02em', margin: 0 }}
							className="text-4xl font-bold text-gray-900 leading-none"
						>
							Paramétrage des emplacements
						</h1>
						<p className="text-sm text-gray-400" style={{ margin: '0.5rem 0 0' }}>
							Créez et modifiez les emplacements et les espaces communs : ils apparaissent aussitôt dans Emplacements
						</p>
					</div>
					<button
						onClick={() => show_add_form ? closeAddForm() : setShowAddForm(true)}
						className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider text-white shadow-sm transition-all"
						style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
					>
						{show_add_form
							? <XIcon className="h-3.5 w-3.5" />
							: <PlusIcon className="h-3.5 w-3.5" />
						}
						{show_add_form ? 'Fermer' : 'Créer un lieu'}
					</button>
				</div>

				{/* Panneau création — UI complète, branchement serveur à venir */}
				{show_add_form && (
					<motion.section
						initial={{ opacity: 0, y: -8 }}
						animate={{ opacity: form_closing ? 0 : 1, y: form_closing ? -8 : 0, transition: { duration: 0.2 } }}
						className="overflow-hidden mb-8 rounded-2xl shadow-xl border border-slate-800"
							style={{ backgroundColor: '#1e1b4b' }}
						>
							<form
								onSubmit={handleAddPitch}
								className="text-white p-6 md:p-8"
								style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', background: 'none' }}
							>
								{/* En-tête du formulaire */}
								<div className="flex justify-between items-start pb-4 border-b border-white border-opacity-10">
									<div>
										<h3 className="text-sm font-bold uppercase font-mono tracking-widest text-indigo-400 mb-1">
											{form_is_custom ? 'Créer un lieu' : 'Nouvel emplacement'}
										</h3>
										<p className="text-sm font-mono text-slate-400 leading-relaxed">
											{form_is_custom
												? "Ajoutez un espace d'intervention terrain (piscine, restaurant, local technique…)."
												: "Enregistrez un nouvel hébergement client."
											}
										</p>
									</div>
									<button
										type="button"
										onClick={() => closeAddForm()}
										className="p-1.5 rounded-lg text-slate-400 hover:text-white transition-colors"
										style={{ backgroundImage: 'none', backgroundColor: 'rgba(255,255,255,0.05)' }}
									>
										<XIcon className="h-4 w-4" />
									</button>
								</div>

								{/* Switcher Lieu sur mesure / Hébergement PMS */}
								<div className="flex p-1 rounded-xl max-w-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
									<button
										type="button"
										onClick={() => switchFormMode(true)}
										className="flex-1 py-2 px-3 text-sm font-bold uppercase tracking-wider rounded-lg transition-all"
										style={{ backgroundImage: 'none', backgroundColor: form_is_custom ? '#4f46e5' : 'transparent', color: form_is_custom ? 'white' : '#94a3b8' }}
									>
										<span role="img" aria-label="lieu">📍</span> Lieu d'intervention
									</button>
									<button
										type="button"
										onClick={() => switchFormMode(false)}
										className="flex-1 py-2 px-3 text-sm font-bold uppercase tracking-wider rounded-lg transition-all"
										style={{ backgroundImage: 'none', backgroundColor: !form_is_custom ? '#4f46e5' : 'transparent', color: !form_is_custom ? 'white' : '#94a3b8' }}
									>
										<span role="img" aria-label="camping">⛺️</span> Hébergement
									</button>
								</div>

								{/* Champs du formulaire */}
								<div className="grid grid-cols-1 md:grid-cols-5 gap-4">

									{/* Nom */}
									<div>
										<label className="block text-sm font-bold text-indigo-200 uppercase tracking-wider mb-1.5">
											{form_is_custom ? 'Nom du lieu' : 'N° emplacement PMS'}
										</label>
										<input
											type="text"
											value={new_pitch.name}
											onChange={e => { const v = e.target.value; setNewPitch(prev => ({ ...prev, name: v })) }}
											className="pitches-create-input"
											placeholder={form_is_custom ? 'Ex : Local piscine, Restaurant…' : 'Ex : Chalet 4'}
											required
										/>
									</div>

									{/* Catégorie */}
									<div>
										<label className="block text-sm font-bold text-indigo-200 uppercase tracking-wider mb-1.5">Catégorie</label>
										<select
											value={new_pitch.category}
											onChange={e => { const v = e.target.value; setNewPitch(prev => ({ ...prev, category: v })) }}
											className="pitches-create-select"
										>
											{form_is_custom
												? <>
													{/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
													<option value="Local Technique">⚙️ Local Technique</option>
													{/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
													<option value="Espace Aquatique">🏊 Espace Aquatique</option>
													{/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
													<option value="Restauration / Commerce">🍽️ Restauration / Commerce</option>
													{/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
													<option value="Sanitaires / Bloc">🚿 Sanitaires / Blocs</option>
													{/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
													<option value="Sport / Loisirs">⚽ Sport / Loisirs</option>
													{/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
													<option value="Administration / Accueil">🏢 Administration / Accueil</option>
													{/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
													<option value="Espace Vert">🌳 Espace Vert</option>
													{/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
													<option value="Autre">❓ Autre lieu…</option>
												</>
												: unique_categories.map(c => <option key={c} value={c}>{c}</option>)
											}
										</select>
									</div>

									{/* Zone */}
									<div>
										<label className="block text-sm font-bold text-indigo-200 uppercase tracking-wider mb-1.5">Zone d'implantation</label>
										{new_pitch_zone_free
											? <div className="flex items-center gap-1.5">
												<input
													type="text"
													value={new_pitch.zone === '—' ? '' : new_pitch.zone}
													onChange={e => { const v = e.target.value; setNewPitch(prev => ({ ...prev, zone: v })) }}
													className="pitches-create-input flex-1"
													placeholder="Nom de la zone…"
													autoFocus
												/>
												<button
													type="button"
													onClick={() => { setNewPitchZoneFree(false); setNewPitch(prev => ({ ...prev, zone: '—' })) }}
													className="p-1.5 rounded-lg transition-colors"
													style={{ backgroundImage: 'none', backgroundColor: 'rgba(255,255,255,0.08)', color: '#94a3b8' }}
													title="Retour à la liste"
												>
													<RefreshIcon className="h-3.5 w-3.5" />
												</button>
											</div>
											: <select
												value={new_pitch.zone}
												onChange={e => {
													const v = e.target.value
													if (v === '__autre__') { setNewPitchZoneFree(true); setNewPitch(prev => ({ ...prev, zone: '' })) }
													else setNewPitch(prev => ({ ...prev, zone: v }))
												}}
												className="pitches-create-select"
											>
												<option value="—">— (Aucune zone)</option>
												{unique_zones_for_filter.map(z => <option key={z} value={z}>{z}</option>)}
												<option value="__autre__">Autre…</option>
											</select>
										}
									</div>

									{/* Type */}
									<div>
										<label className="block text-sm font-bold text-indigo-200 uppercase tracking-wider mb-1.5">Type</label>
										{form_is_custom
											? <div className="pitches-create-locked">
												<span role="img" aria-label="outil">🔧</span> Intervention (Hors-PMS)
											</div>
											: <select
												value={new_pitch.type}
												onChange={e => { const v = e.target.value; setNewPitch(prev => ({ ...prev, type: v })) }}
												className="pitches-create-select"
											>
												<option value="location">location (Hébergement)</option>
												<option value="resident">resident (Résidentiel)</option>
												<option value="nu">nu (Emplacement nu)</option>
											</select>
										}
									</div>

									{/* Capacité */}
									<div>
										<label className="block text-sm font-bold text-indigo-200 uppercase tracking-wider mb-1.5">Capacité d'accueil</label>
										{form_is_custom
											? <div className="pitches-create-locked">
												Hors-réservation (0 pers.)
											</div>
											: <input
												type="number"
												min="0"
												max="50"
												value={new_pitch.capacity}
												onChange={e => { const v = e.target.value; setNewPitch(prev => ({ ...prev, capacity: v })) }}
												className="pitches-create-input"
												required
											/>
										}
									</div>
								</div>

								{/* Actions */}
								<div className="flex justify-end gap-3 pt-4 border-t border-white border-opacity-10">
									<button
										type="button"
										onClick={() => closeAddForm()}
										className="px-5 py-2 rounded-lg text-sm font-bold uppercase tracking-wider transition-all"
										style={{ backgroundImage: 'none', backgroundColor: 'rgba(255,255,255,0.05)', color: '#cbd5e1' }}
									>
										Annuler
									</button>
									<button
										type="submit"
										className="px-6 py-2 rounded-lg text-sm font-bold uppercase tracking-wider text-white transition-all"
										style={{ backgroundImage: 'none', backgroundColor: '#4f46e5' }}
									>
										Valider la création
									</button>
								</div>
							</form>
					</motion.section>
				)}

				{/* Stats cards */}
				<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
					<div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
						<div className="w-10 h-10 rounded-lg bg-orange-50 border border-orange-100 text-orange-500 flex items-center justify-center shrink-0">
							<CollectionIcon className="h-5 w-5" />
						</div>
						<div>
							<p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Catégories</p>
							<p className="text-lg font-black text-slate-800 font-mono mt-0.5">{unique_categories.length}</p>
						</div>
					</div>

					<div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
						<div className="w-10 h-10 rounded-lg bg-green-50 border border-green-100 text-green-500 flex items-center justify-center shrink-0">
							<LocationMarkerIcon className="h-5 w-5" />
						</div>
						<div>
							<p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Zones</p>
							<p className="text-lg font-black text-slate-800 font-mono mt-0.5">{unique_zones_count}</p>
						</div>
					</div>

					<div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
						<div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 text-blue-500 flex items-center justify-center shrink-0">
							<UsersIcon className="h-5 w-5" />
						</div>
						<div>
							<p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Capacité cumulée</p>
							<p className="text-lg font-black text-slate-800 font-mono mt-0.5">
								{total_capacity} <span className="text-sm font-medium text-slate-500">pers.</span>
							</p>
						</div>
					</div>

					<div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
						<div className="w-10 h-10 rounded-lg bg-purple-50 border border-purple-100 text-purple-500 flex items-center justify-center shrink-0">
							<CogIcon className="h-5 w-5" />
						</div>
						<div>
							<p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Lieux Hors-PMS</p>
							<p className="text-lg font-black text-purple-600 font-mono mt-0.5">{custom_count}</p>
						</div>
					</div>
				</div>

				{/* Barre recherche + filtres */}
				<div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-row items-center gap-4 mb-6">
					<div className="relative flex-1">
						<SearchIcon className="absolute left-3.5 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" />
						<input
							type="text"
							value={search_term}
							onChange={e =>
							{
								setSearchTerm(e.target.value)
								setCurrentPage(1)
							}}
							className="pitches-search-input w-full"
							placeholder="Rechercher par N° emplacement, catégorie, zone, type…"
						/>
						{search_term !== '' && (
							<button
								onClick={() => setSearchTerm('')}
								className="absolute right-3.5 top-2.5 text-slate-400 hover:text-slate-600"
								style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
							>
								<XIcon className="h-4 w-4" />
							</button>
						)}
					</div>

					<div className="flex items-center gap-3 shrink-0">
						<div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
							<AdjustmentsIcon className="h-3.5 w-3.5 text-slate-400" />
							<span className="text-sm font-bold text-slate-400 uppercase tracking-wider">Filtres</span>
						</div>

						<select
							value={selected_type}
							onChange={e =>
							{
								setSelectedType(e.target.value)
								setCurrentPage(1)
							}}
							className="pitches-filter-select"
						>
							<option value="all">Tous les types</option>
							{unique_types_for_filter.map(t => <option key={t} value={t}>{t}</option>)}
						</select>

						<select
							value={selected_zone}
							onChange={e =>
							{
								setSelectedZone(e.target.value)
								setCurrentPage(1)
							}}
							className="pitches-filter-select"
						>
							<option value="all">Toutes les zones</option>
							{unique_zones_for_filter.map(z => <option key={z} value={z}>{z}</option>)}
						</select>
					</div>
				</div>

				{/* Tableau */}
				<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
					{filtered_pitches.length === 0
						? (
							<div className="p-16 text-center">
								<ExclamationCircleIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
								<p className="text-sm font-bold text-slate-700 mb-1">Aucun emplacement trouvé</p>
								<p className="text-sm text-slate-400 mb-4">Réessayez avec une recherche différente.</p>
								{has_filters && (
									<button
										onClick={resetFilters}
										className="text-sm font-bold text-indigo-500 hover:underline"
										style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
									>
										Réinitialiser les filtres
									</button>
								)}
							</div>
						)
						: (
							<div className="overflow-x-auto">
								<table className="pitches-table w-full text-left">
									<thead>
										<tr className="bg-slate-50 border-b border-slate-100">
											<th className="px-6 py-4 text-sm font-black text-slate-400 uppercase tracking-widest">N° emplacement</th>
											<th className="px-6 py-4 text-sm font-black text-slate-400 uppercase tracking-widest">Catégorie</th>
											<th className="px-6 py-4 text-sm font-black text-slate-400 uppercase tracking-widest">Zone</th>
											<th className="px-6 py-4 text-sm font-black text-slate-400 uppercase tracking-widest">Type</th>
											<th className="px-6 py-4 text-sm font-black text-slate-400 uppercase tracking-widest">Capacité</th>
											<th className="px-6 py-4 text-sm font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-slate-100">
										{paginated.map(pitch => renderRow(pitch))}
									</tbody>
								</table>
							</div>
						)
					}

					{/* Pagination */}
					{total_pages > 1 && (
						<div className="px-6 py-4 border-t border-slate-100 bg-slate-50 bg-opacity-50 flex flex-col sm:flex-row items-center justify-between gap-4">
							<span className="text-sm font-semibold text-slate-400 font-mono">
								{Math.min(filtered_pitches.length, (safe_page - 1) * ITEMS_PER_PAGE + 1)}–{Math.min(filtered_pitches.length, safe_page * ITEMS_PER_PAGE)} sur {filtered_pitches.length}
							</span>
							<div className="flex items-center gap-1">
								<button
									disabled={safe_page === 1}
									onClick={() => handleSetPage(safe_page - 1)}
									className="p-2 border border-slate-200 hover:bg-slate-50 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
									style={{ backgroundImage: 'none', backgroundColor: 'white' }}
								>
									<ChevronLeftIcon className="h-4 w-4 text-slate-600" />
								</button>

								{Array.from({ length: total_pages }, (_, i) => i + 1)
									.filter(p => p === 1 || p === total_pages || Math.abs(p - safe_page) <= 1)
									.map((page, index, arr) =>
									{
										const show_ellipsis = index > 0 && page - arr[index - 1] > 1
										return (
											<Fragment key={page}>
												{show_ellipsis && <span className="px-2 text-slate-400 font-bold text-sm">…</span>}
												<button
													onClick={() => handleSetPage(page)}
													className="w-8 h-8 rounded-lg text-sm font-bold transition-all"
													style={{ backgroundImage: 'none', backgroundColor: page === safe_page ? '#2563eb' : 'white', color: page === safe_page ? 'white' : '#475569', border: page === safe_page ? 'none' : '1px solid #e2e8f0' }}
												>
													{page}
												</button>
											</Fragment>
										)
									})
								}

								<button
									disabled={safe_page === total_pages}
									onClick={() => handleSetPage(safe_page + 1)}
									className="p-2 border border-slate-200 hover:bg-slate-50 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
									style={{ backgroundImage: 'none', backgroundColor: 'white' }}
								>
									<ChevronRightIcon className="h-4 w-4 text-slate-600" />
								</button>
							</div>
						</div>
					)}
				</div>

				{/* Compteur bas de page */}
				<div className="mt-4">
					<span className="text-sm text-slate-400">
						{pitches.length !== filtered_pitches.length
							? `${filtered_pitches.length} résultat${filtered_pitches.length > 1 ? 's' : ''} sur ${pitches.length} emplacements`
							: `${pitches.length} emplacement${pitches.length > 1 ? 's' : ''} au total`
						}
					</span>
				</div>

			</div>

			{/* Modal de suppression */}
				{modal_delete.isOpen && (
					<div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ backgroundColor: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)' }}>
						<motion.div
							initial={{ opacity: 0, scale: 0.95, y: 15 }}
							animate={{ opacity: 1, scale: 1, y: 0 }}
							className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-slate-200"
						>
							<div className="w-12 h-12 bg-red-50 text-red-500 rounded-xl flex items-center justify-center mb-4">
								<TrashIcon className="h-6 w-6" />
							</div>
							<h3 className="text-lg font-bold text-slate-900 mb-2">Supprimer l'emplacement ?</h3>
							<p className="text-slate-500 text-sm mb-6 leading-relaxed">
								L'emplacement <span className="font-bold text-slate-800 font-mono">"{modal_delete.pitch_id}"</span> sera définitivement supprimé. Cette action est irréversible.
							</p>
							<div className="grid grid-cols-2 gap-3">
								<button
									type="button"
									disabled={modal_delete.submitting}
									onClick={() => setModalDelete({ isOpen: false, pitch_id: null, submitting: false })}
									className="py-2.5 px-4 rounded-xl text-sm font-bold uppercase tracking-wider disabled:opacity-50"
									style={{ backgroundImage: 'none', backgroundColor: '#f1f5f9', color: '#475569' }}
								>
									Annuler
								</button>
								<button
									type="button"
									disabled={modal_delete.submitting}
									onClick={confirmDelete}
									className="py-2.5 px-4 rounded-xl text-sm font-bold uppercase tracking-wider text-white flex items-center justify-center gap-1.5 disabled:opacity-50"
									style={{ backgroundImage: 'none', backgroundColor: '#dc2626' }}
								>
									{modal_delete.submitting
										? <>
											<svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
												<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
											</svg>
											Suppression…
										</>
										: 'Confirmer'
									}
								</button>
							</div>
						</motion.div>
					</div>
				)}

		</Fragment>
	)
}
